const exported = {
  CREDIT_CARD_SAFETY_LINK: 'https://learner.coursera.help/hc/articles/208280176' as const,
  EMAIL_PREFERENCE_LOGO_SIZE: 64,
  PARTNER_BANNER_STATUS_RECEIVED: 'partnerBannerStatusReceived' as const,

  // Please use capture groups with an "or" character inside this regex for additional email address patterns
  // Example: /(@coursera\.org$)|(^andrewng@deeplearning\.org$)/
  TWO_FACTOR_AUTH_EMAIL_ADDRESS_REGEX:
    /(@coursera\.org$)|(@support\.coursera\.org$)|(@cloud\.coursera\.org$)|(@24-7intouch\.com$)/,

  Actions: {
    HIDE_DELETE_CREDIT_CARD_MODAL: 'hideDeleteCreditCardModal',
    SHOW_DELETE_CREDIT_CARD_MODAL: 'showDeleteCreditCardModal',

    RECEIVED_CREDIT_CARD_PREFERENCES: 'receivedCreditCardPreferences',
    RECEIVED_PAYMENT_WALLETS: 'receivedPaymentWallets',
    RECEIVED_EMAIL_PREFERENCES: 'receivedEmailPreferences',
    RECEIVED_USER_SETTINGS: 'receivedUserSettings',
    RESET_SECTION: 'resetSection',
    RESET_ALL_SECTIONS: 'resetAllSections',
    UPDATED_BASIC_INFORMATION: 'updatedBasicInformation',
    UPDATED_EMAIL_ADDRESS: 'updatedEmailAddress',

    UPDATE_FAILED: 'updateFailed',
    UPDATE_FAILED_NAME_MALFORMED: 'updateFailedNameMalformed',
    UPDATE_FAILED_EMAIL_IN_USE: 'updateFailedEmailInUse',
    UPDATE_FAILED_EMAIL_ADDRESS: 'updateFailedEmailAddress',
    UPDATE_FIELD_FAILED: 'updateFieldFailed',
    UPDATE_PASSWORD_FAILED_INSUFFICIENTLY_COMPLEX: 'updatePasswordFailedInsufficientlyComplex',
    UPDATE_STARTED: 'updateStarted',
    UPDATE_SUCCEEDED: 'updateSucceeded',

    UNSUBSCRIBED_ALL_EMAILS: 'unsubscribedAllEmails',
    UPDATE_PENDING_CHANGES_STATUS: 'updatePendingChangesStatus',

    CANCEL_TWO_FACTOR_AUTHENTICATION_ACTION: 'cancelTwoFactorAuthenticationAction',
    DISABLE_TWO_FACTOR_AUTHENTICATION: 'disableTwoFactorAuthentication',
    ENABLE_TWO_FACTOR_AUTHENTICATION: 'enableTwoFactorAuthentication',
    LOAD_TWO_FACTOR_AUTHENTICATION_SETTINGS: 'loadTwoFactorAuthenticationSettings',
    LOADING_TWO_FACTOR_AUTHENTICATION_SETTINGS: 'loadingTwoFactorAuthenticationSettings',
    PERFORMING_TWO_FACTOR_AUTHENTICATION_SERVER_ACTION: 'performingTwoFactorAuthenticationServerAction',
    RECEIVED_INVALID_PASSWORD: 'receivedInvalidPassword',
    RECEIVED_SUCCESSFUL_DISABLING_TWO_FACTOR_AUTHENTICATION_RESPONSE:
      'receivedSuccessfulDisablingTwoFactorAuthenticationResponse',
    RECEIVED_TWO_FACTOR_AUTHENTICATION_CODE_CONFIRMATION_RESPONSE:
      'receivedTwoFactorAuthenticationCodeConfirmationResponse',
    RECEIVED_TWO_FACTOR_AUTHENTICATION_QR_CODE: 'receivedTwoFactorAuthenticationQRCode',
    RECEIVED_TWO_FACTOR_AUTHENTICATION_SETTINGS: 'receivedTwoFactorAuthenticationSettings',
    ACCOUNT_DELETED: 'accountDeleted',
    SET_TOP_SECTION: 'setTopSection',
  } as const,

  Fields: {
    EMAIL_ADDRESS: 'email_address',
    EMAIL_PREFERENCES: 'email_preferences',
    FULL_NAME: 'fullName',
    ID: 'id',
    LOCALE: 'locale',
    NEW_PASSWORD: 'new_password',
    PASSWORD: 'password',
    TIMEZONE: 'timezone',
    WALLET: 'wallet',
    ALTERNATIVE_EMAIL: 'alternative_email',
    CODE: 'code',
  } as const,

  FieldGroups: {
    PROFILE: 'profile', // user settings group including FULL_NAME, TIMEZONE, LOCALE
  } as const,

  PartnerBannerStatus: {
    DISMISSED: 'dismissed',
    SUBSCRIBED: 'subscribed',
    UNSUBSCRIBED: 'unsubscribed',
  } as const,

  TopSections: {
    ACCOUNT: 'Account',
    PAYMENT: 'Payment',
    EMAIL: 'Email',
    HIGHLIGHTS: 'Highlights',
    CALENDAR: 'Calendar',
  } as const,

  Sections: {
    BASIC_INFORMATION: 'basicInformation',
    CREDIT_CARD_DELETE: 'creditCardDelete',
    COURSE_EMAIL_PREFERENCES: 'courseEmailPreferences',
    COURSERA_EMAIL_PREFERENCES: 'courseraEmailPreferences',
    GLOBAL_EMAIL_PREFERENCES: 'globalEmailPreferences',
    PARTNER_EMAIL_PREFERENCES: 'partnerEmailPreferences',
    PASSWORD: 'password',
    PERSONAL_ACCOUNT: 'personalAccount',
  } as const,

  Subscriptions: {
    DISABLED: 'DISABLED',
    ENABLED: 'ENABLED',
    SUBSCRIBED: 'SUBSCRIBED',
    UNSUBSCRIBED: 'UNSUBSCRIBED',
  } as const,

  TwoFactorAuthenticationStatus: {
    DISABLED: 'disabled',
    DISABLING_CONFIRMING_PASSWORD: 'disablingConfirmingPasword',
    ENABLED: 'enabled',
    ENABLING_CONFIRMING_PASSWORD: 'enablingConfirmingPassword',
    ENABLING_CONFIRMING_CODE: 'enablingConfirmingCode',
    LOADING: 'loading',
    UNAVAILABLE: 'unavailable',
  } as const,

  UpdateStates: {
    FAILED: 'failed',
    FAILED_NEW_PASSWORD_NOT_SUFFICIENTLY_COMPLEX: 'passwordNotComplex',
    FAILED_NAME_MALFORMED: 'failedNameMalformed',
    FAILED_EMAIL_IN_USE: 'failedEmailInUse',
    FAILED_EMAIL_ADDRESS: 'failedEmailAddress',
    FAILED_SAME_PASSWORD: 'failedSamePassword',
    IDLE: 'idle',
    SUCCEEDED: 'succeeded',
    UPDATING: 'updating',
  } as const,

  PaymentMethodSectionDisplayMode: {
    ENTER_CARD: 'enterCard',
    VIEW_CURRENT_CARD: 'viewCurrentCard',
  } as const,
};

export type ProfileFieldType =
  | typeof exported.Fields.FULL_NAME
  | typeof exported.Fields.TIMEZONE
  | typeof exported.Fields.LOCALE;

export type ProfileSetting = {
  type: ProfileFieldType;
  value?: string;
};

export default exported;

export const {
  CREDIT_CARD_SAFETY_LINK,
  EMAIL_PREFERENCE_LOGO_SIZE,
  PARTNER_BANNER_STATUS_RECEIVED,
  Actions,
  Fields,
  FieldGroups,
  PartnerBannerStatus,
  TopSections,
  Sections,
  Subscriptions,
  TwoFactorAuthenticationStatus,
  UpdateStates,
  PaymentMethodSectionDisplayMode,
} = exported;
